import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Search, RotateCcw, ClipboardList, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const OPERATIONS = [
  'Processing Inward',
  'Folding',
  'Pressing',
  'Segregation',
  'Damage Checking',
  'Assortment Packing',
];

const OP_COLORS = {
  'Processing Inward': 'bg-indigo-100 text-indigo-700',
  'Folding': 'bg-orange-100 text-orange-700',
  'Pressing': 'bg-rose-100 text-rose-700',
  'Segregation': 'bg-lime-100 text-lime-700',
  'Damage Checking': 'bg-amber-100 text-amber-700',
  'Assortment Packing': 'bg-cyan-100 text-cyan-700',
};

const slug = (s) => s.toLowerCase().replace(/ /g, '-');

const InhouseOperationsReport = () => {
  const navigate = useNavigate();

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedOps, setSelectedOps] = useState([...OPERATIONS]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const buildParams = (df, dt, ops) => {
    const params = {};
    if (df) params.date_from = df;
    if (dt) params.date_to = dt;
    if (ops && ops.length > 0 && ops.length < OPERATIONS.length) params.operations = ops.join(',');
    return params;
  };

  const fetchReport = async (targetPage = 1, overrides = {}) => {
    const df = overrides.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
    const dt = overrides.dateTo !== undefined ? overrides.dateTo : dateTo;
    const ops = overrides.ops !== undefined ? overrides.ops : selectedOps;
    if (ops && ops.length === 0) {
      setData({ rows: [], total: 0, total_qty: 0, page: 1, page_size: pageSize, date_from: df, date_to: dt, operations: [] });
      setPage(1);
      return;
    }
    setLoading(true);
    try {
      const params = { ...buildParams(df, dt, ops), page: targetPage, page_size: pageSize };
      const r = await apiClient.get('/inhouse/report/operations-log', { params });
      setData(r.data);
      setPage(targetPage);
      // First load / Clear: backend defaults to today (IST) — sync inputs from the echo
      if (!df && r.data?.date_from) setDateFrom(r.data.date_from);
      if (!dt && r.data?.date_to) setDateTo(r.data.date_to);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchReport(1); }, []);

  const handleSearch = () => fetchReport(1);

  const handleClear = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedOps([...OPERATIONS]);
    fetchReport(1, { dateFrom: '', dateTo: '', ops: [...OPERATIONS] });
  };

  const toggleOp = (op) => {
    setSelectedOps((prev) => (prev.includes(op) ? prev.filter((o) => o !== op) : [...prev, op]));
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { ...buildParams(dateFrom, dateTo, selectedOps), format: 'csv' };
      const r = await apiClient.get('/inhouse/report/operations-log', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `inhouse_operations_${dateFrom || 'today'}_to_${dateTo || 'today'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalQty = data?.total_qty || 0;
  const totalPages = Math.max(1, Math.ceil(total / (data?.page_size || pageSize)));

  return (
    <div className="min-h-screen bg-gray-50" data-testid="inhouse-ops-report-page">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <ClipboardList className="w-5 h-5 text-slate-600" />
        <div className="flex-1">
          <h1 className="text-lg font-bold text-gray-900">Operations Report</h1>
          <p className="text-xs text-gray-500">
            Processing Inward · Folding · Pressing · Segregation · Damage Checking · Assortment Packing — times in IST
          </p>
        </div>
        <Button
          onClick={exportCsv}
          disabled={exporting || loading || total === 0}
          variant="outline"
          className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4 mr-1.5" />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Filters */}
        <Card>
          <CardContent className="pt-4 space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">From (IST)</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-44"
                  data-testid="date-from-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">To (IST)</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-44"
                  data-testid="date-to-input"
                />
              </div>
              <Button onClick={handleSearch} disabled={loading} data-testid="search-btn">
                <Search className="w-4 h-4 mr-1.5" />
                {loading ? 'Loading…' : 'Search'}
              </Button>
              <Button onClick={handleClear} variant="outline" disabled={loading} data-testid="clear-btn">
                <RotateCcw className="w-4 h-4 mr-1.5" />
                Clear
              </Button>
            </div>

            {/* Operation filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Operations</label>
              <div className="flex flex-wrap gap-2" data-testid="op-filter">
                {OPERATIONS.map((op) => {
                  const active = selectedOps.includes(op);
                  return (
                    <button
                      key={op}
                      onClick={() => toggleOp(op)}
                      className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold border transition-all ${
                        active
                          ? `${OP_COLORS[op]} border-transparent ring-1 ring-inset ring-gray-300`
                          : 'bg-white text-gray-500 border-gray-200 hover:text-gray-700'
                      }`}
                      data-testid={`op-checkbox-${slug(op)}`}
                      title={active ? 'Click to hide this operation' : 'Click to include this operation'}
                    >
                      <span className={`w-2 h-2 rounded-full mr-1.5 ${active ? 'bg-current' : 'bg-gray-300'}`} />
                      {op}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="flex flex-wrap items-center gap-3">
          <Card className="flex-1 min-w-[160px]">
            <CardContent className="py-3">
              <p className="text-xs text-gray-500">Rows</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="summary-total">{total}</p>
            </CardContent>
          </Card>
          <Card className="flex-1 min-w-[160px]">
            <CardContent className="py-3">
              <p className="text-xs text-gray-500">Total Qty (pieces)</p>
              <p className="text-2xl font-bold text-indigo-700" data-testid="summary-qty">{totalQty}</p>
            </CardContent>
          </Card>
          <Card className="flex-1 min-w-[220px]">
            <CardContent className="py-3">
              <p className="text-xs text-gray-500">Range (IST)</p>
              <p className="text-sm font-semibold text-gray-800" data-testid="summary-range">
                {data?.date_from || '—'} → {data?.date_to || '—'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ops-report-table">
                <thead>
                  <tr className="text-left text-gray-600 bg-gray-50 border-b">
                    <th className="px-3 py-2.5 font-medium">#</th>
                    <th className="px-3 py-2.5 font-medium">Date (IST)</th>
                    <th className="px-3 py-2.5 font-medium">Time (IST)</th>
                    <th className="px-3 py-2.5 font-medium">Sort No</th>
                    <th className="px-3 py-2.5 font-medium">FG Barcode</th>
                    <th className="px-3 py-2.5 font-medium">Design Name</th>
                    <th className="px-3 py-2.5 font-medium">Operation</th>
                    <th className="px-3 py-2.5 font-medium text-right">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={8} className="px-3 py-10 text-center text-gray-400">Loading…</td></tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-12 text-center text-gray-500" data-testid="ops-empty">
                        No operations recorded for the selected range.
                      </td>
                    </tr>
                  )}
                  {!loading && rows.map((r, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-gray-50" data-testid={`ops-row-${i}`}>
                      <td className="px-3 py-2 text-gray-400">{(page - 1) * pageSize + i + 1}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.date || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{r.time || '-'}</td>
                      <td className="px-3 py-2 font-mono">{r.sort_no || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 font-mono">{r.fg_barcode || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2">{r.design_name || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2">
                        <Badge className={`${OP_COLORS[r.operation] || 'bg-gray-100 text-gray-700'} border-0 text-[11px]`}>
                          {r.operation}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">{r.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500" data-testid="page-info">
              Page {page} of {totalPages} · {total} rows
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1 || loading}
                onClick={() => fetchReport(page - 1)}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages || loading}
                onClick={() => fetchReport(page + 1)}
                data-testid="next-page-btn"
              >
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InhouseOperationsReport;
